import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { api } from '../api/client';

interface User {
  email: string;
}

interface LayoutProps {
  children: React.ReactNode;
  user: User;
  onLogout: () => Promise<void>;
}

const navItems = [
  { to: '/', label: 'Dashboard', icon: '▦', enabled: true },
  { to: '/analytics', label: 'Analytics', icon: '▤', enabled: true },
  { to: '/health', label: 'Health', icon: '◈', enabled: true },
  { to: '/diary', label: 'Diary', icon: '▣', enabled: true },
  { to: '/handlers', label: 'Handlers', icon: '◉', enabled: true },
  { to: '/changes', label: 'Changes', icon: '△', enabled: true },
  { to: '/precedent-search', label: 'Precedent', icon: '⊕', enabled: true },
  { to: '/bdx', label: 'BDX Lloyd\'s', icon: '⬡', enabled: true },
  { to: '/bdx-coverholder', label: 'BDX Billing', icon: '⬢', enabled: true },
  { to: '/loss-runs', label: 'Loss Runs', icon: '⬢', enabled: true },
  { to: '/convex', label: 'Convex Loss Run', icon: '◆', enabled: true },
  { to: '/sla-reporting', label: 'SLA Reporting', icon: '◇', enabled: true },
  { to: '/guide', label: 'Guide', icon: '◐', enabled: true },
  { to: '/users', label: 'Users', icon: '◎', enabled: true },
  { to: '/admin/programs', label: 'Programs', icon: '⚙', enabled: true },
  { to: '/admin/queue-triage', label: 'Queue Triage', icon: '⊘', enabled: true },
  { to: '/admin', label: 'Admin', icon: '◈', enabled: true },
];

export default function Layout({ children, user, onLogout }: LayoutProps) {
  const [signingOut, setSigningOut] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const fetchPending = () => {
      api.getChangeStats()
        .then((data: any) => setPendingCount(data.pending || 0))
        .catch(() => {});
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await onLogout();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside
        className="flex flex-col w-64 flex-shrink-0"
        style={{ backgroundColor: '#1e293b' }}
      >
        {/* Branding */}
        <div className="flex items-center gap-2 px-5 py-5 border-b border-slate-700">
          <span className="text-xl leading-none" aria-hidden="true">🛡</span>
          <div>
            <div className="text-white font-bold text-base leading-tight tracking-wide">
              FileHandler
            </div>
            <div className="text-slate-400 text-xs font-medium tracking-widest uppercase">
              Claims Intelligence
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) =>
            item.enabled ? (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150',
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                  ].join(' ')
                }
              >
                <span className="text-base leading-none w-4 text-center" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
                {item.to === '/changes' && pendingCount > 0 && (
                  <span className="ml-auto bg-amber-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            ) : (
              <div
                key={item.to}
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 cursor-not-allowed select-none"
                title="Coming soon"
              >
                <span className="text-base leading-none w-4 text-center" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
                <span className="ml-auto text-xs text-slate-600 font-normal">Soon</span>
              </div>
            )
          )}
        </nav>

        {/* User info + logout */}
        <div className="px-4 py-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
              style={{ backgroundColor: '#3b82f6', color: '#fff' }}
            >
              {user.email.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div
                className="text-slate-200 text-xs font-medium truncate"
                title={user.email}
              >
                {user.email}
              </div>
              <div className="text-slate-500 text-xs">Signed in</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            disabled={signingOut}
            className="w-full text-xs text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded px-3 py-1.5 text-left transition-colors duration-150"
          >
            {signingOut ? 'Signing out...' : 'Sign out'}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-gray-50">
        {children}
      </main>
    </div>
  );
}
